import React, { useEffect, useState } from 'react';
import {
  Card,
  List,
  Tag,
  Typography,
  Button,
  Space,
  Spin,
  Empty,
  Badge,
  Row,
  Col,
  Statistic,
} from 'antd';
import {
  MailOutlined,
  CalendarOutlined,
  UserOutlined,
  DownOutlined,
  UpOutlined,
} from '@ant-design/icons';
import { useCondoStore } from '../../store/condoStore';
import { useAuthStore } from '../../store/authStore';
import { useAuth } from '../../../../context/UnifiedAuthContext';
import { EmailSchedule, EmailType } from '../../types';

import { formatDate as formatDateShared } from '../../utils/date';
const { Title, Text, Paragraph } = Typography;

// ── label / tag-color per emailType ─────────────────────────────────────────

interface TypeMeta {
  label: string;
  tagColor: string;
  iconBg: string;
}

const EMAIL_TYPE_META: Record<EmailType, TypeMeta> = {
  FINANCIAL_STATEMENT: { label: 'Financial Statement', tagColor: 'blue',    iconBg: '#1677ff' },
  FEE_REMINDER:        { label: 'Fee Reminder',         tagColor: 'gold',    iconBg: '#faad14' },
  VIOLATION_NOTICE:    { label: 'Violation Notice',     tagColor: 'red',     iconBg: '#f5222d' },
  MAINTENANCE_NOTICE:  { label: 'Maintenance',          tagColor: 'orange',  iconBg: '#fa8c16' },
  MEETING_NOTICE:      { label: 'Board Meeting',        tagColor: 'green',   iconBg: '#52c41a' },
  GENERAL_ANNOUNCEMENT:{ label: 'Announcement',         tagColor: 'purple',  iconBg: '#722ed1' },
  ARREARS_NOTICE:      { label: 'Arrears Notice',       tagColor: 'volcano', iconBg: '#f5222d' },
};

const RECIPIENT_TYPE_LABEL: Record<string, string> = {
  ALL_UNITS:       'All Units',
  SPECIFIC_UNITS:  'Specific Units',
  UNITS_IN_ARREARS:'Units in Arrears',
};

// ── helpers ──────────────────────────────────────────────────────────────────

function unitInRecipientIds(unitNumber: string, recipientUnitIds: string): boolean {
  const raw = recipientUnitIds.trim();

  // Try JSON array first
  if (raw.startsWith('[')) {
    try {
      const arr: string[] = JSON.parse(raw);
      return arr.map(String).includes(unitNumber);
    } catch {
      // fall through to comma-split
    }
  }

  // Comma-separated string
  return raw
    .split(',')
    .map((s) => s.trim())
    .includes(unitNumber);
}

// Sprint 145 (R14-10) — this file grew its own `formatDate`, one of four
// divergent date treatments in the tree. It is kept as a thin local alias only
// so the many call sites below stay unchanged; the rule itself now lives in
// `utils/date.ts`, which parses a bare `YYYY-MM-DD` as a LOCAL calendar date
// (`new Date(str)` parses it as UTC midnight, hence the −1-day renders) and
// formats it as en-CA rather than following the browser to US `M/D/YYYY`.
const formatDate = formatDateShared;

// ── sub-component: single message card ───────────────────────────────────────

interface MessageCardProps {
  email: EmailSchedule;
  condoName: string;
}

const MessageCard: React.FC<MessageCardProps> = ({ email, condoName }) => {
  const [expanded, setExpanded] = useState(false);
  const meta = EMAIL_TYPE_META[email.emailType] ?? EMAIL_TYPE_META.GENERAL_ANNOUNCEMENT;

  return (
    <Card
      style={{
        marginBottom: 12,
        borderRadius: 10,
        transition: 'box-shadow 0.2s',
      }}
      styles={{ body: { padding: '16px 20px' } }}
      hoverable
    >
      <Row gutter={12} wrap={false} align="top">
        {/* Colored icon circle */}
        <Col flex="none">
          <div
            style={{
              width: 44,
              height: 44,
              borderRadius: '50%',
              background: meta.iconBg,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <MailOutlined style={{ color: '#fff', fontSize: 20 }} />
          </div>
        </Col>

        {/* Content */}
        <Col flex="auto" style={{ minWidth: 0 }}>
          {/* Row 1: subject + type tag + date */}
          <Row justify="space-between" align="middle" wrap gutter={[8, 4]}>
            <Col>
              <Text strong style={{ fontSize: 15 }}>
                {email.subject}
              </Text>
            </Col>
            <Col>
              <Space size={8}>
                <Tag color={meta.tagColor} style={{ margin: 0 }}>
                  {meta.label}
                </Tag>
                <Text type="secondary" style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
                  <CalendarOutlined style={{ marginRight: 4 }} />
                  {formatDate(email.lastSentAt)}
                </Text>
              </Space>
            </Col>
          </Row>

          {/* Row 2: sender */}
          <Row style={{ marginTop: 4 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>
              <UserOutlined style={{ marginRight: 4 }} />
              From: {condoName} Management
              {email.createdByName ? ` · ${email.createdByName}` : ''}
            </Text>
          </Row>

          {/* Body (collapsible) */}
          <div style={{ marginTop: 10 }}>
            <Paragraph
              style={{
                marginBottom: 0,
                whiteSpace: 'pre-wrap',
                overflow: 'hidden',
                maxHeight: expanded ? undefined : 60,
              }}
            >
              {email.body}
            </Paragraph>
            <Button
              type="link"
              size="small"
              style={{ padding: 0, marginTop: 4 }}
              icon={expanded ? <UpOutlined /> : <DownOutlined />}
              onClick={() => setExpanded((v) => !v)}
            >
              {expanded ? 'Show less' : 'Read more'}
            </Button>
          </div>

          {/* Bottom tags */}
          <Row style={{ marginTop: 10 }} gutter={[6, 6]}>
            <Col>
              <Tag color={meta.tagColor}>{meta.label}</Tag>
            </Col>
            <Col>
              <Tag color="default">
                {RECIPIENT_TYPE_LABEL[email.recipientType] ?? email.recipientType}
              </Tag>
            </Col>
          </Row>
        </Col>
      </Row>
    </Card>
  );
};

// ── main component ────────────────────────────────────────────────────────────

export const OwnerCorrespondence: React.FC = () => {
  const { user } = useAuth();
  const { currentCondo } = useAuthStore();
  const { emailSchedules, fetchEmailSchedules, owners } = useCondoStore();
  const [loading, setLoading] = useState(false);

  // Fetch on mount when we have a condoId
  useEffect(() => {
    if (!currentCondo?.id) return;
    setLoading(true);
    fetchEmailSchedules(currentCondo.id).finally(() => setLoading(false));
  }, [currentCondo?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Resolve the owner's unit number
  const ownerRecord = owners.find((o) => o.userId === user?.id);
  const unitNumber = ownerRecord?.unitNumber ?? '';

  // Filter emails visible to this owner
  const visibleEmails: EmailSchedule[] = emailSchedules
    .filter((e) => {
      if (e.status !== 'SENT') return false;
      if (e.recipientType === 'ALL_UNITS') return true;
      if (e.recipientType === 'UNITS_IN_ARREARS') return true;
      if (e.recipientType === 'SPECIFIC_UNITS') {
        if (!e.recipientUnitIds || !unitNumber) return false;
        return unitInRecipientIds(unitNumber, e.recipientUnitIds);
      }
      return false;
    })
    .slice()
    .sort((a, b) => {
      const da = a.lastSentAt ? new Date(a.lastSentAt).getTime() : 0;
      const db = b.lastSentAt ? new Date(b.lastSentAt).getTime() : 0;
      return db - da;
    });

  const mostRecentDate =
    visibleEmails.length > 0 ? formatDate(visibleEmails[0].lastSentAt) : '—';

  const condoName = currentCondo?.name ?? 'Building';

  return (
    <div style={{ padding: '24px 0', maxWidth: 860, margin: '0 auto' }}>
      {/* Page header */}
      <Row align="middle" style={{ marginBottom: 24 }} gutter={12}>
        <Col>
          <div
            style={{
              width: 48,
              height: 48,
              borderRadius: 12,
              background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <MailOutlined style={{ color: '#fff', fontSize: 24 }} />
          </div>
        </Col>
        <Col>
          <Title level={3} style={{ margin: 0, lineHeight: 1.2 }}>
            My Correspondence
          </Title>
          <Text type="secondary">Messages from your building management</Text>
        </Col>
      </Row>

      {/* Stats bar */}
      {!loading && visibleEmails.length > 0 && (
        <Row gutter={16} style={{ marginBottom: 20 }}>
          <Col>
            <Card
              size="small"
              style={{ borderRadius: 8, minWidth: 140 }}
              styles={{ body: { padding: '12px 16px' } }}
            >
              <Statistic
                title="Total Messages"
                value={visibleEmails.length}
                prefix={<Badge color="#4f46e5" />}
              />
            </Card>
          </Col>
          <Col>
            <Card
              size="small"
              style={{ borderRadius: 8, minWidth: 180 }}
              styles={{ body: { padding: '12px 16px' } }}
            >
              <Statistic
                title="Most Recent"
                value={mostRecentDate}
                valueStyle={{ fontSize: 16 }}
                prefix={<CalendarOutlined style={{ color: '#4f46e5' }} />}
              />
            </Card>
          </Col>
        </Row>
      )}

      {/* Content */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '80px 0' }}>
          <Spin size="large" tip="Loading messages…" />
        </div>
      ) : visibleEmails.length === 0 ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_DEFAULT}
          description={
            <span>
              <Text strong>No messages yet</Text>
              <br />
              <Text type="secondary">
                Messages sent by board members or administrators will appear here.
              </Text>
            </span>
          }
          style={{ padding: '80px 0' }}
        />
      ) : (
        <List
          dataSource={visibleEmails}
          renderItem={(email) => (
            <List.Item style={{ padding: 0, display: 'block' }}>
              <MessageCard email={email} condoName={condoName} />
            </List.Item>
          )}
        />
      )}
    </div>
  );
};
